/**
 * Phone-number helpers. Pure; no I/O.
 *
 * MY-first today (the platform launches in Malaysia). Adding other
 * SEA countries (ID/PH/TH/VN) is straightforward — the same shape
 * applies, just different country-code logic.
 *
 * Two output shapes are needed across the apps:
 *   - **E.164** (`+60xxxxxxxxx`) for storage + Firebase phone OTP
 *   - **digits-only with country code** (`60xxxxxxxxx`) for wa.me links
 *
 * Format display (`formatE164`) is best-effort pretty-print; not a
 * locale-aware libphonenumber substitute (we don't ship libphonenumber
 * because of bundle weight — MVP-scale customers/operators in MY only).
 */

/**
 * Normalise a Malaysia phone to **E.164** (`+60xxxxxxxxx`) or `null`
 * if the input doesn't look like a valid MY mobile.
 *
 * Accepts the four formats users actually type:
 *   - `+60 12-345 6789`  (international with country code, with separators)
 *   - `60123456789`      (international, no separators)
 *   - `0123456789`       (local with leading 0)
 *   - `123456789`        (bare mobile starting with 1)
 *
 * Length rule: post-normalisation the core (without `+`) must be 11–13
 * digits — MY mobiles are `60` + 9 or 10 digits.
 */
export function normaliseMalaysianPhone(raw: string): string | null {
  const digits = raw.replace(/[\s\-()+]/g, '');
  if (!digits) return null;
  let core = digits;
  if (core.startsWith('60')) {
    // Already country-coded
  } else if (core.startsWith('0')) {
    core = '60' + core.slice(1);
  } else if (core.startsWith('1')) {
    // Bare MY mobile (1xx xxx xxxx)
    core = '60' + core;
  } else {
    return null;
  }
  if (core.length < 11 || core.length > 13) return null;
  if (!/^\d+$/.test(core)) return null;
  return '+' + core;
}

/**
 * Lightweight pretty-print of an E.164 MY number for display.
 * Returns the input unchanged if it doesn't look like `+60…`.
 */
export function formatE164(e164: string): string {
  if (!e164.startsWith('+60')) return e164;
  const rest = e164.slice(3);
  if (rest.length === 9) return `+60 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
  if (rest.length === 10) return `+60 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  return e164;
}

/**
 * Convert any MY-formatted phone into the digits-only-with-country-code
 * shape that `wa.me/<digits>` requires (no leading `+`).
 *
 * Returns `null` when normalisation fails — caller renders a fallback
 * ("Phone not on file yet") instead of a broken WhatsApp link.
 */
export function toWaMeDigits(raw: string): string | null {
  const e164 = normaliseMalaysianPhone(raw);
  if (!e164) return null;
  return e164.slice(1); // strip the +
}

/** How many trailing digits stay readable when a number is masked. */
const VISIBLE_TAIL = 4;

/**
 * Mask a phone number for display to STAFF.
 *
 * The operator queue board has to let a cashier call the person whose car is
 * ready. It does not need to publish a full mobile number on a screen that sits
 * on a counter in view of whoever is standing at it — so the ticket carries a
 * pre-masked string and the raw number stays in the customer's own profile.
 *
 * The last four digits are what people read back to confirm identity, and what
 * an operator matches against a WhatsApp thread, so those are what survive.
 *
 * Returns `null` when there is nothing worth showing — empty, or so short that
 * masking would either reveal everything or leave nothing. Callers omit the
 * field rather than render a placeholder: a row reading `••••` with no digits
 * teaches staff to ignore that column.
 *
 * @example
 *   maskPhone('+60123456789') // '+60 •••• 6789'
 *   maskPhone('0123456789')   // '•••• 6789'
 */
export function maskPhone(raw: string | undefined | null): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const plus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < VISIBLE_TAIL + 2) return null;

  const tail = digits.slice(-VISIBLE_TAIL);
  if (!plus) return `•••• ${tail}`;

  // Keep the country code so staff can tell a local number from a foreign one —
  // a Singaporean customer is worth noticing at a Johor carwash. Two digits is
  // right for this market without pretending to parse every country.
  const cc = digits.slice(0, digits.startsWith('60') ? 2 : 1);
  return `+${cc} •••• ${tail}`;
}

/**
 * The first name only, for a queue card.
 *
 * A full legal name on a counter screen is more than the job needs, and a long
 * one breaks the card layout. Returns `null` when there is nothing usable so
 * the caller omits the element instead of rendering an empty one.
 */
export function firstNameOnly(raw: string | undefined | null): string | null {
  if (typeof raw !== 'string') return null;
  const first = raw.trim().split(/\s+/)[0];
  if (first === undefined || first === '') return null;
  return first.slice(0, 24);
}
